/**
 * Product Variants
 * Handles multi-variant quantity selection and add to cart functionality.
 */

if (!customElements.get('product-variants')) {
  class ProductVariants extends HTMLElement {
    constructor() {
      super();
      this.variantsList = this.querySelector('.product-variants__list');
      this.addToCartButton = this.querySelector('[data-add-to-cart]');
      this.addToCartLabel = this.querySelector('[data-add-to-cart-label]');
      this.floatingButton = document.querySelector('[data-floating-add-to-cart]');
      this.floatingLabel = document.querySelector('[data-floating-label]');
      this.minicart = document.querySelector('mini-cart');
      this.queue = [];
      this.isProcessing = false;
      this.currentGroupId = null;
    }

    connectedCallback() {
      this.bindEvents();
      this.initButtonStates();
      this.updateTotalPrice();
    }

    /**
     * Binds event listeners
     */
    bindEvents() {
      this.variantsList.addEventListener('click', (event) => {
        const button = event.target.closest('[data-action]');
        if (button) {
          this.handleQuantityButton(button, event);
        }

        const preorderButton = event.target.closest('[data-preorder-toggle]');
        if (preorderButton) {
          this.handlePreorderToggle(preorderButton, event);
        }
      });

      this.variantsList.addEventListener('change', (event) => {
        if (event.target.matches('.product-variants__qty-input')) {
          this.handleQuantityChange(event.target);
        }
      });

      document.addEventListener('bespoke:change', () => this.updateTotalPrice());

      if (this.addToCartButton) {
        this.addToCartButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.addToCart();
        });
      }

      /**
       * Listen for add-on selection changes to update total price
       */
      document.addEventListener('addon:selected', () => {
        this.updateTotalPrice();
      });

      document.addEventListener('addon:deselected', () => {
        this.updateTotalPrice();
      });
    }

    /**
     * Handles +/- button clicks
     * @param {HTMLElement} button - The clicked button
     * @param {Event} event - The click event
     */
    handleQuantityButton(button, event) {
      event.preventDefault();
      const action = button.dataset.action;
      const quantityContainer = button.closest('.product-variants__quantity');
      const input = quantityContainer.querySelector('.product-variants__qty-input');
      const currentValue = parseInt(input.value) || 0;
      const min = parseInt(input.min) || 0;
      const max = input.max ? parseInt(input.max) : null;

      if (action === 'increase') {
        if (max === null || currentValue < max) {
          input.value = currentValue + 1;
        }
      } else if (action === 'decrease' && currentValue > min) {
        input.value = currentValue - 1;
      }

      this.updateButtonStates(quantityContainer);
      this.updateTotalPrice();
      this.dispatchQuantityChange();
    }

    /**
     * Handles pre-order button toggle click
     * @param {HTMLElement} button - The pre-order button
     * @param {Event} event - The click event
     */
    handlePreorderToggle(button, event) {
      event.preventDefault();
      const variantItem = button.closest('.product-variants__item');
      if (variantItem) {
        variantItem.classList.toggle('product-variants__item--preorder-active');
      }
    }

    /**
     * Handles direct quantity input changes
     * @param {HTMLInputElement} input - The quantity input
     */
    handleQuantityChange(input) {
      const value = parseInt(input.value) || 0;
      const min = parseInt(input.min) || 0;
      const max = input.max ? parseInt(input.max) : null;

      if (value < min) {
        input.value = min;
      } else if (max !== null && value > max) {
        input.value = max;
      }

      const quantityContainer = input.closest('.product-variants__quantity');
      this.updateButtonStates(quantityContainer);
      this.updateTotalPrice();
      this.dispatchQuantityChange();
    }

    /**
     * Updates plus/minus button disabled states based on quantity
     * @param {HTMLElement} container - The quantity container element
     */
    updateButtonStates(container) {
      const input = container.querySelector('.product-variants__qty-input');
      const minusBtn = container.querySelector('[data-action="decrease"]');
      const plusBtn = container.querySelector('[data-action="increase"]');
      const value = parseInt(input.value) || 0;
      const min = parseInt(input.min) || 0;
      const max = input.max ? parseInt(input.max) : null;

      minusBtn.disabled = value <= min;
      plusBtn.disabled = max !== null && value >= max;
    }

    /**
     * Initializes all quantity button states
     */
    initButtonStates() {
      const containers = this.variantsList.querySelectorAll('.product-variants__quantity');
      containers.forEach((container) => {
        this.updateButtonStates(container);
      });
    }

    /**
     * Calculates total price and item count from all quantity inputs
     * @returns {Object} Object with totalPrice and itemCount
     */
    calculateTotals() {
      let totalPrice = 0;
      let itemCount = 0;
      const inputs = this.variantsList.querySelectorAll('.product-variants__qty-input');

      inputs.forEach((input) => {
        const quantity = parseInt(input.value) || 0;
        if (quantity > 0) {
          const variantItem = input.closest('.product-variants__item');
          const price = parseInt(variantItem.dataset.variantPrice) || 0;
          totalPrice += price * quantity;
          itemCount += quantity;
        }
      });

      /**
       * Add add-on prices to total
       */
      const addonTotal = this.calculateAddonTotal();
      totalPrice += addonTotal;

      /**
       * Add bespoke option price adjustments (per unit)
       */
      const bespokeComponent = document.querySelector('product-bespoke-options');
      if (bespokeComponent?.getPriceAdjustment) {
        totalPrice += bespokeComponent.getPriceAdjustment() * itemCount;
      }

      return { totalPrice, itemCount };
    }

    /**
     * Calculates total price from selected add-ons
     * @returns {number} Total add-on price in cents
     */
    calculateAddonTotal() {
      let addonTotal = 0;
      const allAddons = document.querySelectorAll('product-addons');

      /**
       * Sum selections across all add-on components (required + optional)
       * selections is Map<categoryId, Map<variantId, selectionData>>
       */
      allAddons.forEach(productAddons => {
        if (!productAddons.selections) return;

        productAddons.selections.forEach((categorySelections) => {
          categorySelections.forEach((selection) => {
            addonTotal += (selection.price * selection.quantity);
          });
        });
      });

      return addonTotal;
    }

    /**
     * Formats price in cents to display string
     * @param {number} cents - Price in cents
     * @returns {string} Formatted price string
     */
    formatPrice(cents) {
      const currency = window.Shopify?.currency?.active || 'GBP';
      const amount = cents / 100;

      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency
      }).format(amount);
    }

    /**
     * Updates the button label and enabled state based on selected items
     */
    updateTotalPrice() {
      const { totalPrice, itemCount } = this.calculateTotals();
      const hasItems = itemCount > 0;

      if (this.addToCartButton) {
        const addText = this.addToCartButton.dataset.addText;
        const chooseText = this.addToCartButton.dataset.chooseText;

        if (hasItems) {
          const formattedPrice = this.formatPrice(totalPrice);
          this.addToCartLabel.textContent = `${addText} – ${formattedPrice}`;
        } else {
          this.addToCartLabel.textContent = chooseText;
        }

        this.addToCartButton.disabled = !hasItems;
        this.addToCartButton.classList.toggle('disabled', !hasItems);
      }

      if (this.floatingLabel && this.addToCartLabel) {
        this.floatingLabel.textContent = this.addToCartLabel.textContent;
      }

      this.dispatchVariantChange(hasItems, itemCount);
    }

    /**
     * Dispatches variant change event for add-ons integration
     * @param {boolean} hasSelection - Whether any variants have quantity > 0
     * @param {number} totalQuantity - Total quantity across all variants
     */
    dispatchVariantChange(hasSelection, totalQuantity) {
      document.dispatchEvent(new CustomEvent('variant:change', {
        detail: {
          hasSelection,
          totalQuantity
        }
      }));
    }

    /**
     * Dispatches quantity change event for add-ons integration
     */
    dispatchQuantityChange() {
      const { itemCount } = this.calculateTotals();

      document.dispatchEvent(new CustomEvent('quantity:change', {
        detail: {
          totalQuantity: itemCount
        }
      }));
    }

    /**
     * Collects all items to add to cart
     * @returns {Array} Array of items with id, quantity, and properties
     */
    collectItemsToAdd() {
      const items = [];
      const inputs = this.variantsList.querySelectorAll('.product-variants__qty-input');

      const productAddons = document.querySelector('product-addons');
      const hasAddons = productAddons?.selections?.size > 0;

      if (hasAddons) {
        this.currentGroupId = Date.now().toString();
      } else {
        this.currentGroupId = null;
      }

      inputs.forEach((input) => {
        const quantity = parseInt(input.value) || 0;
        if (quantity > 0) {
          const variantId = input.dataset.variantId;
          const variantItem = input.closest('.product-variants__item');
          const properties = {};

          if (variantItem.dataset.preorder === 'true') {
            properties._preOrder = 'true';
            if (variantItem.dataset.dueDates) {
              properties._dueDates = variantItem.dataset.dueDates;
            }
          }

          if (variantItem.dataset.nextDay === 'true') {
            properties.next_day = 'true';
          }

          if (hasAddons) {
            properties.GroupId = this.currentGroupId;
            properties.groupedProduct = 'true';
            properties.tlp = 'true';
            properties.min = String(quantity);
          }

          /**
           * Merge bespoke option selections as cart line item properties
           */
          const bespokeComponent = document.querySelector('product-bespoke-options');
          if (bespokeComponent?.getCartProperties) {
            const bespokeProps = bespokeComponent.getCartProperties();
            Object.assign(properties, bespokeProps);
          }

          items.push({
            id: variantId,
            quantity,
            properties
          });
        }
      });

      return items;
    }

    /**
     * Adds all selected variants to cart
     */
    addToCart() {
      if (this.isProcessing) return;

      /**
       * Validate bespoke options before proceeding
       */
      const bespokeComponent = document.querySelector('product-bespoke-options');
      if (bespokeComponent && !bespokeComponent.isValid) {
        bespokeComponent.showValidationErrors();
        return;
      }

      const items = this.collectItemsToAdd();
      if (items.length === 0) return;

      this.isProcessing = true;
      this.setLoadingState(true);
      this.queue = [...items];
      this.processQueue();
    }

    /**
     * Processes the queue of items to add
     */
    processQueue() {
      if (this.queue.length === 0) {
        this.onQueueComplete();
        return;
      }

      const item = this.queue.shift();
      this.addItemToCart(item)
        .then(() => {
          this.processQueue();
        })
        .catch((error) => {
          console.error('Error adding item to cart:', error);
          this.processQueue();
        });
    }

    /**
     * Adds a single item to the cart via fetch
     * @param {Object} item - Item with id, quantity, and properties
     * @returns {Promise}
     */
    addItemToCart(item) {
      const formData = new FormData();
      formData.append('id', item.id);
      formData.append('quantity', item.quantity);

      if (item.properties && Object.keys(item.properties).length > 0) {
        Object.entries(item.properties).forEach(([key, value]) => {
          formData.append(`properties[${key}]`, value);
        });
      }

      if (this.minicart) {
        const sections = this.minicart.getSectionsToRender().map((section) => section.section);
        formData.append('sections', sections);
        formData.append('sections_url', window.location.pathname);
      }

      const config = {
        method: 'POST',
        headers: {
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      return fetch(window.routes.cart_add_url, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            throw new Error(response.description || 'Error adding to cart');
          }
          return response;
        });
    }

    /**
     * Called when all items have been added
     */
    onQueueComplete() {
      this.isProcessing = false;
      this.setLoadingState(false);

      /**
       * Publish cart update BEFORE resetting quantities.
       * resetQuantities → updateTotalPrice → dispatches variant:change
       * with hasSelection=false → product-addons.disableAddons() clears
       * selections. Publishing first lets add-on components read their
       * selections and build the add-on item list synchronously before
       * the selections are wiped.
       */
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-variants',
          groupId: this.currentGroupId
        });
      }

      this.resetQuantities();

      const bespokeComponent = document.querySelector('product-bespoke-options');
      if (bespokeComponent?.reset) {
        bespokeComponent.reset();
      }

      this.updateTotalPrice();

      if (this.minicart) {
        this.minicart.updateSectionContents();
        if (!window.themeSettings?.redirectToCart) {
          this.minicart.open(this.addToCartButton);
        } else {
          window.location.href = window.routes.cart_url;
        }
      }
    }

    /**
     * Resets all quantity inputs to 0
     */
    resetQuantities() {
      const inputs = this.variantsList.querySelectorAll('.product-variants__qty-input');
      inputs.forEach((input) => {
        input.value = 0;
      });
      this.initButtonStates();
    }

    /**
     * Sets loading state on buttons
     * @param {boolean} isLoading - Whether loading state should be active
     */
    setLoadingState(isLoading) {
      if (this.addToCartButton) {
        this.addToCartButton.classList.toggle('loading', isLoading);
        this.addToCartButton.disabled = isLoading;
      }

      if (this.floatingButton) {
        this.floatingButton.classList.toggle('loading', isLoading);
        this.floatingButton.disabled = isLoading;
      }
    }
  }

  customElements.define('product-variants', ProductVariants);
}
